import React, { useEffect, useState } from 'react';
import { DollarSign, LinkIcon, Search } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Button } from '../../../shared/components/Button';
import { Can } from '../../../shared/components/Can';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Skeleton } from '../../../shared/components/Skeleton';
import { TextInput } from '../../../shared/components/TextInput';
import { FEATURES } from '../../../shared/lib/permissions';
import type { TalentPoolCandidate } from '../api';
import { useBancoTalentos, useEtapas, useVagas, useVincularCandidatoAVaga } from '../hooks';
import { maskCurrencyBRL } from '../../../shared/utils/validators';
import { currencyInputToApi, formatCurrencyBRL } from '../components/format';
import { RecrutamentoModal } from '../components/RecrutamentoModal';
import {
  VagaVinculadaCampos,
  vagaOptionDescription,
  vagaOptionLabel,
} from '../components/VagaVinculadaCampos';

/**
 * Banco de talentos: candidatos reprovados ou não aproveitados, com
 * consentimento vigente, prontos para reaproveitamento.
 *
 * Quem não consentiu a retenção simplesmente não aparece — o filtro é do
 * servidor, para que uma tela nova não possa esquecê-lo.
 */
export default function BancoTalentosPage(): React.ReactElement {
  const [searchTerm, setSearchTerm] = useState('');
  const [search, setSearch] = useState('');
  const [salaryTo, setSalaryTo] = useState('');
  const [vincular, setVincular] = useState<TalentPoolCandidate | null>(null);
  const [vagaEscolhida, setVagaEscolhida] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, isError } = useBancoTalentos({
    search: search || undefined,
    salaryTo: currencyInputToApi(salaryTo),
    limit: 50,
  });
  const { data: etapas } = useEtapas();
  const { data: vagas } = useVagas({ status: 'publicada', limit: 100 });
  const vincularMutation = useVincularCandidatoAVaga();

  const vagaSelecionada = (vagas?.data ?? []).find((vaga) => vaga.id === vagaEscolhida) ?? null;
  const labels = new Map((etapas ?? []).map((etapa) => [etapa.key, etapa.label]));
  const candidatos = data?.data ?? [];

  return (
    <PageContainer
      title="Banco de talentos"
      subtitle="Candidatos disponíveis para reaproveitamento em novas vagas"
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[240px] flex-1">
          <TextInput
            label="Buscar por nome, cargo ou cidade"
            icon={<Search className="h-4 w-4" />}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        <div className="w-48">
          <TextInput
            label="Pretensão até"
            inputMode="numeric"
            icon={<DollarSign className="h-4 w-4" />}
            value={salaryTo}
            onChange={(event) => setSalaryTo(maskCurrencyBRL(event.target.value))}
          />
        </div>
      </div>

      {isError && <Alert variant="error">Não foi possível carregar o banco de talentos.</Alert>}

      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : candidatos.length === 0 ? (
        <EmptyState
          title="Banco de talentos vazio"
          description="Candidatos reprovados ou não aproveitados, com consentimento vigente, aparecem aqui."
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Nome</th>
                <th className="px-3 py-2 text-left">Cargo</th>
                <th className="px-3 py-2 text-left">Cidade</th>
                <th className="px-3 py-2 text-right">Pretensão</th>
                <th className="px-3 py-2 text-left">Parou em</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {candidatos.map((candidato) => (
                <tr key={candidato.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-900">{candidato.fullName}</td>
                  <td className="px-3 py-2">{candidato.positionOfInterest ?? '—'}</td>
                  <td className="px-3 py-2">{candidato.city ?? '—'}</td>
                  <td className="px-3 py-2 text-right">
                    {formatCurrencyBRL(candidato.expectedSalary)}
                  </td>
                  <td className="px-3 py-2">
                    {candidato.lastStageKey
                      ? (labels.get(candidato.lastStageKey) ?? candidato.lastStageKey)
                      : '—'}
                    {candidato.lastClosingReason && (
                      <p className="text-xs text-gray-500">{candidato.lastClosingReason}</p>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Can feature={FEATURES.recrutamentoCandidatos} action="edit">
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={<LinkIcon className="h-3.5 w-3.5" />}
                        onClick={() => {
                          setVincular(candidato);
                          setVagaEscolhida('');
                        }}
                      >
                        Vincular a vaga
                      </Button>
                    </Can>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {vincular && (
        <RecrutamentoModal
          open
          title="Vincular a uma vaga"
          description={`${vincular.fullName} entra na etapa inicial do funil, reaproveitando o cadastro e o currículo já enviados.`}
          submitLabel="Vincular"
          isSubmitting={vincularMutation.isPending}
          submitError={(vincularMutation.error as Error | null)?.message ?? null}
          canSubmit={Boolean(vagaEscolhida)}
          onClose={() => setVincular(null)}
          onSubmit={() =>
            vincularMutation.mutate(
              { candidateId: vincular.id, jobOpeningId: vagaEscolhida, source: 'banco_talentos' },
              { onSuccess: () => setVincular(null) },
            )
          }
        >
          <AutocompleteSelect
            label="Vaga pretendida"
            options={(vagas?.data ?? []).map((vaga) => ({
              value: vaga.id,
              label: vagaOptionLabel(vaga),
              description: vagaOptionDescription(vaga),
            }))}
            value={vagaEscolhida}
            onChange={setVagaEscolhida}
          />
          {vagaSelecionada && (
            <div className="grid gap-4 sm:grid-cols-2">
              <VagaVinculadaCampos vaga={vagaSelecionada} />
            </div>
          )}
        </RecrutamentoModal>
      )}

    </PageContainer>
  );
}
